import { pageQuery, type Counted, type PageOpts } from "./paging";
/**
 * Typed CRM API client.
 *
 * Every call goes through the shared `api` wrapper (lib/api.ts) so session
 * cookies, CSRF origin headers, and 401→refresh→retry all ride along.
 * Base path is /api/v1/org/{org_id}/crm — the router prefix in
 * apps/api/app/api/v1/crm.py.
 */

import { api } from "./api";

const ORG = import.meta.env.VITE_ORG_ID ?? "";

function base(orgId?: string): string {
  return `/org/${orgId ?? ORG}/crm`;
}

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export interface Company {
  created_by_user_id?: string | null;
  created_by_email?: string | null;
  updated_by_user_id?: string | null;
  updated_by_email?: string | null;
  id: string;
  company_code?: string | null;
  legal_name: string;
  trading_name?: string | null;
  industry?: string | null;
  company_size_band?: string | null;
  annual_revenue_band?: string | null;
  website?: string | null;
  primary_phone?: string | null;
  primary_email?: string | null;
  registration_number?: string | null;
  vat_number?: string | null;
  billing_address_line1?: string | null;
  billing_address_line2?: string | null;
  billing_address_city?: string | null;
  billing_address_state?: string | null;
  billing_address_postal?: string | null;
  billing_address_country?: string | null;
  shipping_address_line1?: string | null;
  shipping_address_line2?: string | null;
  shipping_address_city?: string | null;
  shipping_address_state?: string | null;
  shipping_address_postal?: string | null;
  shipping_address_country?: string | null;
  currency?: string | null;
  payment_terms?: string | null;
  owner_user_id?: string | null;
  owner_name?: string | null;
  can_reassign_owner?: boolean;
  account_manager_user_id?: string | null;
  sales_person_user_id?: string | null;
  lead_source?: string | null;
  sales_region?: string | null;
  sales_subregion?: string | null;
  account_status?: string | null;
  parent_company_id?: string | null;
  notes?: string | null;
  custom_fields?: Record<string, unknown> | null;
  territory_id?: string | null;
  territory_code?: string | null;
  territory_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CompanyInput {
  legal_name: string;
  trading_name?: string | null;
  industry?: string | null;
  company_size_band?: string | null;
  annual_revenue_band?: string | null;
  website?: string | null;
  primary_phone?: string | null;
  primary_email?: string | null;
  registration_number?: string | null;
  vat_number?: string | null;
  billing_address_line1?: string | null;
  billing_address_line2?: string | null;
  billing_address_city?: string | null;
  billing_address_state?: string | null;
  billing_address_postal?: string | null;
  billing_address_country?: string | null;
  shipping_address_line1?: string | null;
  shipping_address_line2?: string | null;
  shipping_address_city?: string | null;
  shipping_address_state?: string | null;
  shipping_address_postal?: string | null;
  shipping_address_country?: string | null;
  currency?: string | null;
  payment_terms?: string | null;
  owner_user_id?: string | null;
  account_manager_user_id?: string | null;
  sales_person_user_id?: string | null;
  lead_source?: string | null;
  sales_region?: string | null;
  sales_subregion?: string | null;
  account_status?: string | null;
  parent_company_id?: string | null;
  notes?: string | null;
  custom_fields?: Record<string, unknown> | null;
  territory_id?: string | null;
}

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

export interface Contact {
  created_by_user_id?: string | null;
  created_by_email?: string | null;
  updated_by_user_id?: string | null;
  updated_by_email?: string | null;
  id: string;
  company_id: string;
  company_name?: string | null;
  first_name: string;
  last_name?: string | null;
  gender?: string | null;
  job_title?: string | null;
  department?: string | null;
  email?: string | null;
  work_email?: string | null;
  personal_email?: string | null;
  marketing_enabled?: boolean;
  phone?: string | null;
  mobile?: string | null;
  is_primary?: boolean;
  is_billing_contact?: boolean;
  is_active?: boolean;
  notes?: string | null;
  custom_fields?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ContactInput {
  company_id: string;
  first_name: string;
  last_name?: string | null;
  gender?: string | null;
  job_title?: string | null;
  department?: string | null;
  email?: string | null;
  work_email?: string | null;
  personal_email?: string | null;
  marketing_enabled?: boolean;
  phone?: string | null;
  mobile?: string | null;
  is_primary?: boolean;
  is_billing_contact?: boolean;
  is_active?: boolean;
  notes?: string | null;
  custom_fields?: Record<string, unknown> | null;
}

export interface DuplicateMatch {
  matches: Array<Record<string, string | null>>;
  hidden_conflict: boolean;
}

export interface PipelinePreviewDeal {
  id: string;
  title: string;
  company_name?: string | null;
  value: number;
  currency: string;
}

export interface PipelinePreviewColumn {
  key: string;
  label: string;
  count: number;
  value: number;
  deals: PipelinePreviewDeal[];
}

export interface SparklinePoint {
  label: string;
  month?: number;
  value: number;
}

export interface SparklineSeries {
  label: string;
  start: string;
  end: string;
  total: number;
  points: SparklinePoint[];
}

export interface QuotaSnapshot {
  attained: number;
  target: number;
  pct: number;
  ytd_attained: number;
  yearly_target: number;
  period_label: string;
  period_start?: string;
  period_end?: string;
  year_label: string;
  year_start?: string;
  year_end?: string;
  subject: "personal" | "team" | "none";
}

export interface OrgDashboard {
  kpi: {
    open_leads: number;
    hot_leads: number;
    follow_ups_today: number;
    follow_ups_leads?: number;
    follow_ups_cycle?: number;
    activities_today: number;
    open_pipeline_count?: number;
    open_pipeline_value?: number;
    won_this_year?: number;
  };
  pipeline_preview?: PipelinePreviewColumn[];
  quota?: QuotaSnapshot;
  sparklines?: {
    quarter: SparklineSeries;
    ytd: SparklineSeries;
  };
}

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

export interface LeadProduct {
  id: string;
  name: string;
  sku?: string | null;
  unit_price?: number | null;
  currency?: string | null;
  agreement_line_code?: string | null;
  agreement_id?: string | null;
  agreement_number?: string | null;
}

export interface Lead {
  id: string;
  lead_code?: string | null;
  company_id?: string | null;
  contact_id?: string | null;
  salutation?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  job_title?: string | null;
  email?: string | null;
  phone?: string | null;
  company_name?: string | null;
  lead_status?: string | null;
  lead_source?: string | null;
  lead_score?: number | null;
  rating?: string | null;
  amount?: number | null;
  currency?: string | null;
  owner_user_id?: string | null;
  owner_name?: string | null;
  can_reassign_owner?: boolean;
  next_action_at?: string | null;
  next_action_type?: string | null;
  expected_close_date?: string | null;
  notes?: string | null;
  disqualification_reason?: string | null;
  converted_at?: string | null;
  converted_to_company_id?: string | null;
  converted_to_contact_id?: string | null;
  converted_to_opportunity_id?: string | null;
  opportunity_stage?: string | null;
  opportunity_stage_label?: string | null;
  age_days?: number | null;
  products?: LeadProduct[];
  custom_fields?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
  board_hidden_at?: string | null;
}

export interface LeadInput {
  company_id?: string | null;
  new_company_name?: string | null;
  contact_id?: string | null;
  salutation?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  job_title?: string | null;
  email?: string | null;
  phone?: string | null;
  company_name?: string | null;
  lead_status?: string | null;
  lead_source?: string | null;
  lead_score?: number | null;
  rating?: string | null;
  owner_user_id?: string | null;
  next_action_at?: string | null;
  expected_close_date?: string | null;
  notes?: string | null;
  product_ids?: string[];
  custom_fields?: Record<string, unknown> | null;
}

export interface LeadBoardStage {
  value: string;
  label: string;
}

export interface LeadBoard {
  stages: LeadBoardStage[];
  buckets: Record<string, Lead[]>;
  board_display?: BoardDisplaySettings;
  /** Rows matching the filter, including ones past the board's page. */
  total?: number;
}

export type BoardDisplayEntity = "lead" | "opportunity";

export interface BoardDisplaySettings {
  entity: BoardDisplayEntity;
  retain_positive_days: number;
  retain_negative_days: number;
  aging_band1_max_days: number;
  aging_band2_max_days: number;
  aging_band3_max_days: number;
  aging_band1_color: string;
  aging_band2_color: string;
  aging_band3_color: string;
  aging_band4_color: string;
  terminal_positive_color: string;
  terminal_negative_color: string;
  /** Built-in values, returned by GET so the settings panel can show and restore them. */
  defaults?: Omit<BoardDisplaySettings, "entity" | "defaults">;
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export interface Product {
  created_by_user_id?: string | null;
  created_by_email?: string | null;
  updated_by_user_id?: string | null;
  updated_by_email?: string | null;
  id: string;
  sku: string;
  name: string;
  description?: string | null;
  product_category?: string | null;
  unit_price?: number | null;
  currency?: string | null;
  cost_price?: number | null;
  is_active?: boolean;
  product_status?: string | null;
  unit_of_measure?: string | null;
  territory_id?: string | null;
  territory_code?: string | null;
  territory_name?: string | null;
  custom_fields?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ProductInput {
  sku: string;
  name: string;
  description?: string | null;
  product_category?: string | null;
  unit_price?: number | null;
  currency?: string | null;
  cost_price?: number | null;
  is_active?: boolean;
  product_status?: string | null;
  unit_of_measure?: string | null;
  territory_id?: string | null;
  custom_fields?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Opportunities
// ---------------------------------------------------------------------------

export interface OpportunityProduct {
  id: string;
  product_id?: string | null;
  name?: string | null;
  sku?: string | null;
  description?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  currency?: string | null;
  unit_of_measure?: string | null;
  agreement_line_id?: string | null;
  agreement_line_code?: string | null;
  agreement_id?: string | null;
  agreement_number?: string | null;
}

export interface OpportunityProductLineInput {
  product_id: string;
  quantity: number;
}

export interface Opportunity {
  id: string;
  opportunity_code?: string | null;
  title: string;
  company_id: string;
  company_name?: string | null;
  primary_contact_id?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  source_lead_id?: string | null;
  source_lead_code?: string | null;
  source_lead_name?: string | null;
  can_revert_to_lead?: boolean | null;
  value?: number | null;
  currency?: string | null;
  stage?: string | null;
  expected_close_date?: string | null;
  owner_user_id?: string | null;
  probability_pct?: number | null;
  deal_source?: string | null;
  forecast_category?: string | null;
  terminal_outcome?: string | null;
  loss_reason?: string | null;
  owner_name?: string | null;
  can_reassign_owner?: boolean;
  won_at?: string | null;
  lost_at?: string | null;
  age_days?: number | null;
  order_id?: string | null;
  order_number?: string | null;
  board_hidden_at?: string | null;
  products?: OpportunityProduct[];
  custom_fields?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface OpportunityInput {
  title: string;
  company_id: string;
  primary_contact_id?: string | null;
  source_lead_id: string;
  value?: number | null;
  currency?: string | null;
  stage?: string | null;
  expected_close_date?: string | null;
  owner_user_id?: string | null;
  probability_pct?: number | null;
  deal_source?: string | null;
  forecast_category?: string | null;
  product_ids?: string[];
  product_lines?: OpportunityProductLineInput[];
  custom_fields?: Record<string, unknown> | null;
}

export interface StageChangeInput {
  stage: string;
  loss_reason?: string | null;
  loss_reason_codes?: string[] | null;
}

export interface LossReasonOption {
  code: string;
  label: string;
  description?: string | null;
}

export interface OpportunityBoardStage {
  value: string;
  label: string;
}

export interface OpportunityBoard {
  stages: OpportunityBoardStage[];
  buckets: Record<string, Opportunity[]>;
  board_display?: BoardDisplaySettings;
  total?: number;
}

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

export interface QuoteLineItem {
  id?: string;
  product_id?: string | null;
  description?: string | null;
  quantity: number;
  unit_price?: number | null;
  discount_pct?: number;
  currency?: string | null;
  fx_rate?: number | null;
  unit_of_measure?: string | null;
  sort_order?: number;
  agreement_line_code?: string | null;
  agreement_id?: string | null;
  agreement_number?: string | null;
}

export interface CommercialAgreementLine {
  created_by_user_id?: string | null;
  created_by_email?: string | null;
  updated_by_user_id?: string | null;
  updated_by_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  id?: string;
  line_code?: string | null;
  agreement_id?: string;
  product_id: string;
  product_name?: string | null;
  product_sku?: string | null;
  contracted_unit_price?: number | null;
  currency?: string | null;
  discount_pct?: number | null;
  unit_of_measure?: string | null;
  min_qty?: number | null;
  max_qty?: number | null;
  lead_time_days?: number | null;
  tax_exempt?: boolean;
  effective_from?: string | null;
  effective_to?: string | null;
}

export interface CommercialAgreement {
  created_by_user_id?: string | null;
  created_by_email?: string | null;
  updated_by_user_id?: string | null;
  updated_by_email?: string | null;
  id: string;
  agreement_number?: string | null;
  company_id: string;
  company_name?: string | null;
  territory_id?: string | null;
  territory_name?: string | null;
  status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  payment_terms?: string | null;
  delivery_terms?: string | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  lines?: CommercialAgreementLine[];
}

export interface CommercialAgreementInput {
  company_id: string;
  territory_id?: string | null;
  status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  payment_terms?: string | null;
  delivery_terms?: string | null;
  notes?: string | null;
  lines?: CommercialAgreementLine[];
}

export interface Quote {
  id: string;
  order_id?: string | null;
  order_number?: string | null;
  invoice_id?: string | null;
  invoice_number?: string | null;
  order_placed_at?: string | null;
  quote_number?: string | null;
  quote_version?: number | null;
  opportunity_id?: string | null;
  opportunity_code?: string | null;
  opportunity_title?: string | null;
  source_lead_id?: string | null;
  source_lead_code?: string | null;
  company_id: string;
  company_name?: string | null;
  contact_id?: string | null;
  contact_name?: string | null;
  quote_status?: string | null;
  quote_approval_required?: boolean;
  issue_date?: string | null;
  expiry_date?: string | null;
  subtotal?: number | null;
  discount_total?: number | null;
  tax_total?: number | null;
  total?: number | null;
  currency?: string | null;
  notes?: string | null;
  terms?: string | null;
  accepted_at?: string | null;
  accepted_by_contact_id?: string | null;
  owner_user_id?: string | null;
  owner_name?: string | null;
  can_reassign_owner?: boolean;
  custom_fields?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
  line_items?: QuoteLineItem[];
  fx_changes?: Array<{
    base_currency: string;
    quote_currency: string;
    old_rate?: string | null;
    new_rate: string;
    source: string;
    actor_name?: string | null;
    created_at?: string | null;
  }>;
}

export interface QuoteInput {
  opportunity_id?: string | null;
  company_id: string;
  contact_id?: string | null;
  quote_status?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  currency?: string | null;
  notes?: string | null;
  terms?: string | null;
  owner_user_id?: string | null;
  line_items?: QuoteLineItem[];
  custom_fields?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export interface Order {
  id: string;
  invoice_id?: string | null;
  invoice_number?: string | null;
  invoice_issued_at?: string | null;
  order_number?: string | null;
  quote_id?: string | null;
  quote_number?: string | null;
  company_id: string;
  company_name?: string | null;
  contact_id?: string | null;
  opportunity_id?: string | null;
  order_status?: string | null;
  placed_at?: string | null;
  subtotal?: number | null;
  discount_total?: number | null;
  tax_total?: number | null;
  total?: number | null;
  currency?: string | null;
  notes?: string | null;
  owner_user_id?: string | null;
  owner_name?: string | null;
  can_reassign_owner?: boolean;
  custom_fields?: Record<string, unknown> | null;
  contract_agreement_id?: string | null;
  contract_number?: string | null;
  contract_delivery_terms?: string | null;
  contract_payment_terms?: string | null;
  contract_lead_time_days?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  line_items?: QuoteLineItem[];
}

// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------

export interface Activity {
  id: string;
  activity_type?: string | null;
  subject?: string | null;
  body?: string | null;
  company_id?: string | null;
  contact_id?: string | null;
  opportunity_id?: string | null;
  lead_id?: string | null;
  quote_id?: string | null;
  order_id?: string | null;
  occurred_at?: string | null;
  ends_at?: string | null;
  owner_user_id?: string | null;
  outcome?: string | null;
  is_private?: boolean;
  custom_fields?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ActivityInput {
  activity_type?: string | null;
  subject?: string | null;
  body?: string | null;
  company_id?: string | null;
  contact_id?: string | null;
  opportunity_id?: string | null;
  lead_id?: string | null;
  quote_id?: string | null;
  order_id?: string | null;
  occurred_at?: string | null;
  ends_at?: string | null;
  owner_user_id?: string | null;
  outcome?: string | null;
  is_private?: boolean;
  custom_fields?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

export interface RecordChange {
  id: string;
  action: string;
  field_key: string;
  field_label?: string | null;
  old_value?: string | null;
  new_value?: string | null;
  actor_name?: string | null;
  actor_user_id?: string | null;
  created_at?: string | null;
  record_code?: string | null;
}

export interface QuoteVersionSummary {
  version_number: number;
  quote_number: string;
  reason: "edited" | "regenerated_from_opportunity" | "reverted";
  created_by_user_id?: string | null;
  created_at?: string | null;
}

export interface TimelineEvent {
  source: string;
  record_id: string;
  sort_at?: string | null;
  event_type?: string | null;
  title?: string | null;
  description?: string | null;
  actor_user_id?: string | null;
  actor_name?: string | null;
  related_record_type?: string | null;
  related_record_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export interface CompanyDashboard {
  company_id: string;
  kpi: {
    open_pipeline_value: number;
    open_pipeline_count: number;
    won_value: number;
    won_count: number;
    lost_count: number;
    win_rate: number | null;
    open_quotes_count: number;
    open_quotes_value: number;
    orders_count: number;
    orders_value: number;
    activities_30d: number;
  };
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export interface FollowUpLead {
  id: string;
  lead_code?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  rating?: string | null;
  next_action_at?: string | null;
  next_action_type?: string | null;
}

export interface FollowUpTask {
  id: string;
  title?: string | null;
  status?: string | null;
  due_at?: string | null;
  run_id: string;
  company_id?: string | null;
  company_name?: string | null;
}

export interface SearchHit {
  record_type: "company" | "lead" | "opportunity" | "quote" | "support" | string;
  record_id: string;
  title: string;
  subtitle: string;
  company_id?: string | null;
}

export interface RegistryValue {
  value: string;
  label?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export type OwnedRecordKind = "companies" | "leads" | "opportunities" | "quotes" | "orders";

export interface OwnerOption {
  user_id: string;
  display_name: string;
}

export const crmApi = {
  search: (orgId: string, q: string, limit = 8) => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", String(limit));
    return api.get<{ items: SearchHit[] }>(`${base(orgId)}/search?${params.toString()}`);
  },
  // Companies
  listCompanies: ((orgId?: string, q?: string, page?: PageOpts, extra?: { withTotal?: boolean }) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (extra?.withTotal) params.set("with_total", "true");
    pageQuery(params, page);
    const qs = params.toString();
    const path = `${base(orgId)}/companies${qs ? `?${qs}` : ""}`;
    if (extra?.withTotal) return api.get<Counted<Company>>(path);
    return api.get<Company[]>(path);
  }) as {
    (orgId: string, q: string | undefined, page: PageOpts, extra: { withTotal: true }): Promise<Counted<Company>>;
    (orgId?: string, q?: string, page?: PageOpts): Promise<Company[]>;
  },
  getCompany: (companyId: string, orgId?: string) =>
    api.get<Company>(`${base(orgId)}/companies/${companyId}`),
  createCompany: (body: CompanyInput, orgId?: string) =>
    api.post<{ id: string; company_code?: string }>(`${base(orgId)}/companies`, body),
  mergeCompanies: (survivorId: string, sourceId: string, orgId?: string) =>
    api.post<{ survivor_id: string; source_id: string }>(`${base(orgId)}/companies/${survivorId}/merge`, {
      source_id: sourceId,
    }),
  updateCompany: (companyId: string, body: Partial<CompanyInput>, orgId?: string) =>
    api.patch<{ id: string }>(`${base(orgId)}/companies/${companyId}`, body),
  companyDependents: (companyId: string, orgId?: string) =>
    api.get<{
      company_id: string;
      total: number;
      counts: Record<string, number>;
      items: Array<{ key: string; label: string; count: number }>;
    }>(`${base(orgId)}/companies/${companyId}/dependents`),
  deleteCompany: (companyId: string, orgId?: string, cascade = false) =>
    api.delete<{ id: string; soft_deleted: boolean }>(
      `${base(orgId)}/companies/${companyId}${cascade ? "?cascade=true" : ""}`,
    ),

  // Contacts
  listContacts: ((orgId?: string, companyId?: string, q?: string, page?: PageOpts, extra?: { withTotal?: boolean }) => {
    const params = new URLSearchParams();
    if (companyId) params.set("company_id", companyId);
    if (q) params.set("q", q);
    if (extra?.withTotal) params.set("with_total", "true");
    pageQuery(params, page);
    const qs = params.toString();
    const path = `${base(orgId)}/contacts${qs ? `?${qs}` : ""}`;
    if (extra?.withTotal) return api.get<Counted<Contact>>(path);
    return api.get<Contact[]>(path);
  }) as {
    (
      orgId: string,
      companyId: string | undefined,
      q: string | undefined,
      page: PageOpts,
      extra: { withTotal: true },
    ): Promise<Counted<Contact>>;
    (orgId?: string, companyId?: string, q?: string, page?: PageOpts): Promise<Contact[]>;
  },
  createContact: (body: ContactInput, orgId?: string) =>
    api.post<{ id: string }>(`${base(orgId)}/contacts`, body),
  updateContact: (contactId: string, body: Partial<ContactInput>, orgId?: string) =>
    api.patch<{ id: string }>(`${base(orgId)}/contacts/${contactId}`, body),
  deleteContact: (contactId: string, orgId?: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/contacts/${contactId}`),

  // Leads
  listLeads: ((
    orgId?: string,
    statusFilter?: string,
    q?: string,
    companyId?: string,
    page?: PageOpts,
    territoryId?: string,
    extra?: { rating?: string; openOnly?: boolean; withTotal?: boolean },
  ) => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status_filter", statusFilter);
    if (q) params.set("q", q);
    if (companyId) params.set("company_id", companyId);
    if (territoryId) params.set("territory_id", territoryId);
    if (extra?.rating) params.set("rating", extra.rating);
    if (extra?.openOnly) params.set("open_only", "true");
    if (extra?.withTotal) params.set("with_total", "true");
    pageQuery(params, page);
    const qs = params.toString();
    const path = `${base(orgId)}/leads${qs ? `?${qs}` : ""}`;
    if (extra?.withTotal) return api.get<Counted<Lead>>(path);
    return api.get<Lead[]>(path);
  }) as {
    (
      orgId: string,
      statusFilter: string | undefined,
      q: string | undefined,
      companyId: string | undefined,
      page: PageOpts,
      territoryId: string | undefined,
      extra: { rating?: string; openOnly?: boolean; withTotal: true },
    ): Promise<Counted<Lead>>;
    (
      orgId?: string,
      statusFilter?: string,
      q?: string,
      companyId?: string,
      page?: PageOpts,
      territoryId?: string,
      extra?: { rating?: string; openOnly?: boolean },
    ): Promise<Lead[]>;
  },
  listLeadBoard: (orgId?: string, q?: string, territoryId?: string, extra?: { rating?: string; openOnly?: boolean }) => {
    const params = new URLSearchParams({ view: "kanban" });
    if (q) params.set("q", q);
    if (territoryId) params.set("territory_id", territoryId);
    if (extra?.rating) params.set("rating", extra.rating);
    if (extra?.openOnly) params.set("open_only", "true");
    return api.get<LeadBoard>(`${base(orgId)}/leads?${params.toString()}`);
  },
  getBoardDisplay: (orgId: string | undefined, entity: BoardDisplayEntity) =>
    api.get<BoardDisplaySettings>(`${base(orgId)}/${entity === "lead" ? "leads" : "opportunities"}/board-display`),
  putBoardDisplay: (orgId: string | undefined, entity: BoardDisplayEntity, body: Omit<BoardDisplaySettings, "entity"> | BoardDisplaySettings) =>
    api.put<BoardDisplaySettings>(`${base(orgId)}/${entity === "lead" ? "leads" : "opportunities"}/board-display`, body),
  getLossReasons: (orgId?: string) =>
    api.get<{ reasons: LossReasonOption[] }>(`${base(orgId)}/opportunities/loss-reasons`),
  putLossReasons: (orgId: string | undefined, reasons: LossReasonOption[]) =>
    api.put<{ reasons: LossReasonOption[] }>(`${base(orgId)}/opportunities/loss-reasons`, { reasons }),
  getLead: (leadId: string, orgId?: string) =>
    api.get<Lead>(`${base(orgId)}/leads/${leadId}`),
  getLeadHistory: (leadId: string, orgId?: string) =>
    api.get<{ changes: RecordChange[] }>(`${base(orgId)}/leads/${leadId}/history`),
  getLeadTimeline: (leadId: string, orgId?: string) =>
    api.get<TimelineEvent[]>(`${base(orgId)}/leads/${leadId}/timeline`),
  replyToLead: (leadId: string, body: string, orgId?: string) =>
    api.post<{ sent: boolean; to: string }>(`${base(orgId)}/leads/${leadId}/reply`, { body }),
  createLead: (body: LeadInput, orgId?: string) =>
    api.post<{ id: string; lead_code?: string; company_id?: string | null; company_created?: boolean; contact_id?: string | null; contact_created?: boolean }>(`${base(orgId)}/leads`, body),
  updateLead: (leadId: string, body: Partial<LeadInput> & { product_ids?: string[]; comment?: string | null }, orgId?: string) =>
    api.patch<{ id: string }>(`${base(orgId)}/leads/${leadId}`, body),
  changeLeadStatus: (leadId: string, leadStatus: string, orgId?: string, comment?: string) =>
    api.post<{ id: string; lead_status: string }>(`${base(orgId)}/leads/${leadId}/status`, {
      lead_status: leadStatus,
      comment: comment ?? null,
    }),
  deleteLead: (leadId: string, orgId?: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/leads/${leadId}`),
  convertLead: (
    leadId: string,
    createOpportunity = true,
    opportunityTitle?: string,
    orgId?: string,
    value?: number,
    currency?: string,
  ) => {
    const params = new URLSearchParams({ create_opportunity: String(createOpportunity) });
    if (opportunityTitle) params.set("opportunity_title", opportunityTitle);
    if (value != null && value > 0) params.set("value", String(value));
    if (currency) params.set("currency", currency);
    return api.post<{ id: string; lead_code?: string; company_id?: string; contact_id?: string; opportunity_id?: string; opportunity_code?: string }>(
      `${base(orgId)}/leads/${leadId}/convert?${params.toString()}`,
    );
  },

  // Products
  listProducts: ((orgId?: string, q?: string, page?: PageOpts, extra?: { withTotal?: boolean }) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (extra?.withTotal) params.set("with_total", "true");
    pageQuery(params, page);
    const qs = params.toString();
    const path = `${base(orgId)}/products${qs ? `?${qs}` : ""}`;
    if (extra?.withTotal) return api.get<Counted<Product>>(path);
    return api.get<Product[]>(path);
  }) as {
    (orgId: string, q: string | undefined, page: PageOpts, extra: { withTotal: true }): Promise<Counted<Product>>;
    (orgId?: string, q?: string, page?: PageOpts): Promise<Product[]>;
  },
  createProduct: (body: ProductInput, orgId?: string) =>
    api.post<{ id: string }>(`${base(orgId)}/products`, body),
  updateProduct: (productId: string, body: Partial<ProductInput>, orgId?: string) =>
    api.patch<{ id: string }>(`${base(orgId)}/products/${productId}`, body),
  deleteProduct: (productId: string, orgId?: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/products/${productId}`),

  // Commercial agreements
  listAgreements: (orgId?: string, companyId?: string) =>
    api.get<CommercialAgreement[]>(`${base(orgId)}/agreements${companyId ? `?company_id=${encodeURIComponent(companyId)}` : ""}`),
  getAgreement: (agreementId: string, orgId?: string) =>
    api.get<CommercialAgreement>(`${base(orgId)}/agreements/${agreementId}`),
  createAgreement: (body: CommercialAgreementInput, orgId?: string) =>
    api.post<{ id: string; agreement_number?: string }>(`${base(orgId)}/agreements`, body),
  updateAgreement: (agreementId: string, body: CommercialAgreementInput, orgId?: string) =>
    api.patch<{ id: string }>(`${base(orgId)}/agreements/${agreementId}`, body),
  deleteAgreement: (agreementId: string, orgId?: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/agreements/${agreementId}`),

  // Opportunities
  listOpportunities: ((
    orgId?: string,
    stage?: string,
    q?: string,
    page?: PageOpts,
    extra?: { companyId?: string; openOnly?: boolean; withTotal?: boolean },
  ) => {
    const params = new URLSearchParams();
    if (stage) params.set("stage", stage);
    if (q) params.set("q", q);
    if (extra?.companyId) params.set("company_id", extra.companyId);
    if (extra?.openOnly) params.set("open_only", "true");
    if (extra?.withTotal) params.set("with_total", "true");
    pageQuery(params, page);
    const qs = params.toString();
    const path = `${base(orgId)}/opportunities${qs ? `?${qs}` : ""}`;
    if (extra?.withTotal) return api.get<Counted<Opportunity>>(path);
    return api.get<Opportunity[]>(path);
  }) as {
    (
      orgId: string,
      stage: string | undefined,
      q: string | undefined,
      page: PageOpts,
      extra: { companyId?: string; openOnly?: boolean; withTotal: true },
    ): Promise<Counted<Opportunity>>;
    (
      orgId?: string,
      stage?: string,
      q?: string,
      page?: PageOpts,
      extra?: { companyId?: string; openOnly?: boolean },
    ): Promise<Opportunity[]>;
  },
  listOpportunityBoard: (orgId?: string, q?: string, extra?: { openOnly?: boolean }) => {
    const params = new URLSearchParams({ view: "kanban" });
    if (q) params.set("q", q);
    if (extra?.openOnly) params.set("open_only", "true");
    return api.get<OpportunityBoard>(`${base(orgId)}/opportunities?${params.toString()}`);
  },
  getOpportunity: (oppId: string, orgId?: string) =>
    api.get<Opportunity>(`${base(orgId)}/opportunities/${oppId}`),
  getOpportunityHistory: (oppId: string, orgId?: string) =>
    api.get<{ changes: RecordChange[] }>(`${base(orgId)}/opportunities/${oppId}/history`),
  getOpportunityTimeline: (oppId: string, orgId?: string) =>
    api.get<TimelineEvent[]>(`${base(orgId)}/opportunities/${oppId}/timeline`),
  createOpportunity: (body: OpportunityInput, orgId?: string) =>
    api.post<{ id: string; opportunity_code?: string }>(`${base(orgId)}/opportunities`, body),
  updateOpportunity: (oppId: string, body: Partial<OpportunityInput>, orgId?: string) =>
    api.patch<{ id: string }>(`${base(orgId)}/opportunities/${oppId}`, body),
  changeOpportunityStage: (oppId: string, body: StageChangeInput, orgId?: string) =>
    api.post<{ id: string; stage: string }>(`${base(orgId)}/opportunities/${oppId}/stage`, body),
  revertOpportunityToLead: (oppId: string, orgId?: string) =>
    api.post<{ opportunity_id: string; lead_id: string; lead_code?: string | null; lead_status: string }>(
      `${base(orgId)}/opportunities/${oppId}/revert-to-lead`,
    ),
  deleteOpportunity: (oppId: string, orgId?: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/opportunities/${oppId}`),

  // Salesperson (owner) on companies, leads, opportunities, quotes and orders. Reassigning is for sales managers.
  listOwners: (orgId?: string) => api.get<{ items: OwnerOption[] }>(`${base(orgId)}/owners`),
  reassignOwner: (kind: OwnedRecordKind, recordId: string, ownerUserId: string, orgId?: string) =>
    api.put<{ id: string; owner_user_id: string; owner_name: string | null; can_reassign_owner: boolean }>(
      `${base(orgId)}/${kind}/${recordId}/owner`,
      { owner_user_id: ownerUserId },
    ),

  // Quotes
  listQuotes: ((
    orgId?: string,
    statusFilter?: string,
    q?: string,
    opportunityId?: string,
    page?: PageOpts,
    extra?: { companyId?: string; withTotal?: boolean },
  ) => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status_filter", statusFilter);
    if (q) params.set("q", q);
    if (opportunityId) params.set("opportunity_id", opportunityId);
    if (extra?.companyId) params.set("company_id", extra.companyId);
    if (extra?.withTotal) params.set("with_total", "true");
    pageQuery(params, page);
    const qs = params.toString();
    const path = `${base(orgId)}/quotes${qs ? `?${qs}` : ""}`;
    if (extra?.withTotal) return api.get<Counted<Quote>>(path);
    return api.get<Quote[]>(path);
  }) as {
    (
      orgId: string,
      statusFilter: string | undefined,
      q: string | undefined,
      opportunityId: string | undefined,
      page: PageOpts,
      extra: { companyId?: string; withTotal: true },
    ): Promise<Counted<Quote>>;
    (
      orgId?: string,
      statusFilter?: string,
      q?: string,
      opportunityId?: string,
      page?: PageOpts,
      extra?: { companyId?: string },
    ): Promise<Quote[]>;
  },
  generateQuoteFromOpportunity: (opportunityId: string, orgId?: string) =>
    api.post<{ id: string; quote_number?: string; total?: number; opportunity_id?: string }>(
      `${base(orgId)}/opportunities/${opportunityId}/generate-quote`,
      {},
    ),
  createQuote: (body: QuoteInput, orgId?: string) =>
    api.post<{ id: string; quote_number?: string }>(`${base(orgId)}/quotes`, body),
  getQuote: (quoteId: string, orgId?: string) =>
    api.get<Quote>(`${base(orgId)}/quotes/${quoteId}`),
  updateQuote: (quoteId: string, body: Partial<QuoteInput>, orgId?: string) =>
    api.patch<Quote>(`${base(orgId)}/quotes/${quoteId}`, body),
  acceptQuote: (quoteId: string, orgId?: string) =>
    api.post<{ id: string; quote_status: string }>(`${base(orgId)}/quotes/${quoteId}/accept`),
  submitQuote: (quoteId: string, orgId?: string) =>
    api.post<{ id: string; quote_status: string }>(`${base(orgId)}/quotes/${quoteId}/submit`, {}),
  approveQuote: (quoteId: string, comment: string | undefined, orgId?: string) =>
    api.post<{ id: string; quote_status: string }>(`${base(orgId)}/quotes/${quoteId}/approve`, { comment }),
  rejectQuote: (quoteId: string, comment: string | undefined, orgId?: string) =>
    api.post<{ id: string; quote_status: string }>(`${base(orgId)}/quotes/${quoteId}/reject`, { comment }),
  sendQuote: (quoteId: string, orgId?: string) =>
    api.post<{ id: string; quote_status: string; accept_token: string }>(`${base(orgId)}/quotes/${quoteId}/send`, {}),
  reviseQuote: (quoteId: string, orgId?: string) =>
    api.post<Quote>(`${base(orgId)}/quotes/${quoteId}/revise`, {}),
  getQuoteHistory: (quoteId: string, orgId?: string) =>
    api.get<{ changes: RecordChange[] }>(`${base(orgId)}/quotes/${quoteId}/history`),
  listQuoteVersions: (quoteId: string, orgId?: string) =>
    api.get<{ versions: QuoteVersionSummary[] }>(`${base(orgId)}/quotes/${quoteId}/versions`),
  activateQuoteVersion: (quoteId: string, versionNumber: number, orgId?: string) =>
    api.post<Quote>(`${base(orgId)}/quotes/${quoteId}/versions/${versionNumber}/activate`, {}),
  generateOrder: (quoteId: string, orgId?: string) =>
    api.post<{ id: string; order_number: string; quote_id: string }>(
      `${base(orgId)}/quotes/${quoteId}/generate-order`,
      {},
    ),
  deleteQuote: (quoteId: string, orgId?: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/quotes/${quoteId}`),

  // Orders
  listOrders: ((
    orgId?: string,
    statusFilter?: string,
    q?: string,
    page?: PageOpts,
    extra?: { companyId?: string; withTotal?: boolean },
  ) => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status_filter", statusFilter);
    if (q) params.set("q", q);
    if (extra?.companyId) params.set("company_id", extra.companyId);
    if (extra?.withTotal) params.set("with_total", "true");
    pageQuery(params, page);
    const qs = params.toString();
    const path = `${base(orgId)}/orders${qs ? `?${qs}` : ""}`;
    if (extra?.withTotal) return api.get<Counted<Order>>(path);
    return api.get<Order[]>(path);
  }) as {
    (
      orgId: string,
      statusFilter: string | undefined,
      q: string | undefined,
      page: PageOpts,
      extra: { companyId?: string; withTotal: true },
    ): Promise<Counted<Order>>;
    (
      orgId?: string,
      statusFilter?: string,
      q?: string,
      page?: PageOpts,
      extra?: { companyId?: string },
    ): Promise<Order[]>;
  },
  getOrder: (orderId: string, orgId?: string) =>
    api.get<Order>(`${base(orgId)}/orders/${orderId}`),
  updateOrder: (orderId: string, body: Partial<Order>, orgId?: string) =>
    api.patch<{ id: string }>(`${base(orgId)}/orders/${orderId}`, body),
  deleteOrder: (orderId: string, orgId?: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/orders/${orderId}`),

  // Activities
  listActivities: (orgId?: string, opts?: { companyId?: string; activityType?: string }) => {
    const params = new URLSearchParams();
    if (opts?.companyId) params.set("company_id", opts.companyId);
    if (opts?.activityType) params.set("activity_type", opts.activityType);
    const qs = params.toString();
    return api.get<Activity[]>(`${base(orgId)}/activities${qs ? `?${qs}` : ""}`);
  },
  createActivity: (body: ActivityInput, orgId?: string) =>
    api.post<{ id: string }>(`${base(orgId)}/activities`, body),
  updateActivity: (activityId: string, body: Partial<ActivityInput>, orgId?: string) =>
    api.patch<{ id: string }>(`${base(orgId)}/activities/${activityId}`, body),
  deleteActivity: (activityId: string, orgId?: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/activities/${activityId}`),

  // Timeline
  getTimeline: (companyId: string, orgId?: string) =>
    api.get<TimelineEvent[]>(`${base(orgId)}/companies/${companyId}/timeline`),

  // Dashboard
  getDashboard: (companyId: string, orgId?: string) =>
    api.get<CompanyDashboard>(`${base(orgId)}/companies/${companyId}/dashboard`),
  getOrgDashboard: (orgId?: string) =>
    api.get<OrgDashboard>(`${base(orgId)}/dashboard`),
  followUps: (orgId?: string) =>
    api.get<{ leads: FollowUpLead[]; tasks: FollowUpTask[] }>(`${base(orgId)}/follow-ups`),
  findCompanyDuplicates: (orgId: string, query: { legal_name?: string; registration_number?: string; vat_number?: string; email?: string; website?: string }) => {
    const params = new URLSearchParams();
    if (query.email) params.set("email", query.email);
    if (query.website) params.set("website", query.website);
    if (query.legal_name) params.set("legal_name", query.legal_name);
    if (query.registration_number) params.set("registration_number", query.registration_number);
    if (query.vat_number) params.set("vat_number", query.vat_number);
    return api.get<DuplicateMatch>(`${base(orgId)}/duplicates/companies?${params.toString()}`);
  },
  findContactDuplicates: (orgId: string, query: { company_id: string; email?: string; first_name?: string; last_name?: string; mobile?: string }) => {
    const params = new URLSearchParams(query.company_id ? { company_id: query.company_id } : {});
    if (query.email) params.set("email", query.email);
    if (query.first_name) params.set("first_name", query.first_name);
    if (query.last_name) params.set("last_name", query.last_name);
    if (query.mobile) params.set("mobile", query.mobile);
    return api.get<DuplicateMatch>(`${base(orgId)}/duplicates/contacts?${params.toString()}`);
  },
  findProductDuplicates: (orgId: string, query: { sku?: string; name?: string }) => {
    const params = new URLSearchParams();
    if (query.sku) params.set("sku", query.sku);
    if (query.name) params.set("name", query.name);
    return api.get<DuplicateMatch>(`${base(orgId)}/duplicates/products?${params.toString()}`);
  },
  findLeadDuplicates: (orgId: string, query: { company_id?: string; email?: string }) => {
    const params = new URLSearchParams();
    if (query.company_id) params.set("company_id", query.company_id);
    if (query.email) params.set("email", query.email);
    return api.get<DuplicateMatch>(`${base(orgId)}/duplicates/leads?${params.toString()}`);
  },

  // Registry
  getRegistry: (listKey: string, orgId?: string) =>
    api.get<RegistryValue[]>(`${base(orgId)}/registry/${listKey}`),
};
